import React, { useState, useEffect, useCallback } from "react";
import {
    MaterialReactTable,
    MRT_TableOptions,
    useMaterialReactTable,
    type MRT_ColumnDef,
    type MRT_Row,
} from "material-react-table";
import { Box, IconButton, Tooltip, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Container, Row, Accordion } from "react-bootstrap";
import { toast } from "react-toastify";

import {
    ReconCustomerMapping,
    ReconCustomerMappingFormData,
    ReconCustomerMappingFormErrors,
    ReconCustomerMappingFormTouched,
    SelectOption,
} from "../../../Types/CustomerMapType";
import {
    fetchReconCustomerMappings,
    createReconCustomerMapping,
    updateReconCustomerMapping,
    deleteReconCustomerMapping,
    bulkDeleteReconCustomerMappings,
    importReconCustomerMappings,
    EXPORT_URL,
} from "./customermap.api";
import {
    CompactSelect,
    CompactTextField,
} from "../../../Components/SharedComponents/SharedComponents";
import {
    fetchBrokerOptions,
    paperTradingCustomerOptions,
} from "../../Trading/PaperTrading/FecthApis";
import { showDeleteConfirmModal } from "../../../Components/ConfirmModal";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { extractErrorMessage } from "../../../Tools/helpers/helpers";
import BrokerTemplateDownloadButton from "../../../Components/TemplateDownloadButton/BrokerTemplateDownloadButton";
import FileUpload from "../../../Components/FileUpload/FileUpload";
import { handleImportFileUpload } from "../Utils/handleFileUploadSubmit";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import { handleDataDownload } from "../Utils/handleDataDownload";

const FIELD_LABELS: Record<keyof ReconCustomerMappingFormData, string> = {
  customer_company: "Customer Company",
  broker: "Broker",
  external_client_code: "External Client Code",
};

const validateField = (
    field: keyof ReconCustomerMappingFormData,
    value: string
): string => (!value ? `${FIELD_LABELS[field]} is required` : "");

const validateAll = (
    data: ReconCustomerMappingFormData
): ReconCustomerMappingFormErrors => {
    const errors: ReconCustomerMappingFormErrors = {};
    (Object.keys(data) as (keyof ReconCustomerMappingFormData)[]).forEach((key) => {
        const msg = validateField(key, data[key]);
        if (msg) errors[key] = msg;
    });
    return errors;
};

const INITIAL_FORM: ReconCustomerMappingFormData = {
    customer_company: "",
    broker: "",
    external_client_code: "",
};


const ReconciliationCustomerMapping: React.FC = () => {
    const [formData, setFormData] = useState<ReconCustomerMappingFormData>(INITIAL_FORM);
    const [formErrors, setFormErrors] = useState<ReconCustomerMappingFormErrors>({});
    const [touched, setTouched] = useState<ReconCustomerMappingFormTouched>({});

    const [tableData, setTableData] = useState<ReconCustomerMapping[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [brokerOptions, setBrokerOptions] = useState<SelectOption[]>([]);
    const [customerOptions, setCustomerOptions] = useState<SelectOption[]>([]);
    const [isBrokerLoading, setIsBrokerLoading] = useState(false);
    const [isCustomerLoading, setIsCustomerLoading] = useState(false);

    const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

    const [openFileUpload, setOpenFileUpload] = useState<boolean>(false);
    const handleFileUploadOpen = () => setOpenFileUpload(true);
    const handleFileUploadClose = () => setOpenFileUpload(false);

    const loadBrokerOptions = useCallback(async () => {
        setIsBrokerLoading(true);
        try {
            const { data, error } = await fetchBrokerOptions();
            if (error) {
                toast.error(`Error loading brokers: ${error}`);
                setBrokerOptions([]);
            } else {
                setBrokerOptions(
                    Array.isArray(data)
                        ? data.map((item: any) => ({ label: item.name, value: item.id }))
                        : []
                );
            }
        } catch {
            toast.error("Failed to load broker options");
            setBrokerOptions([]);
        } finally {
            setIsBrokerLoading(false);
        }
    }, []);

    const loadCustomerOptions = useCallback(async () => {
        setIsCustomerLoading(true);
        try {
            const { data, error } = await paperTradingCustomerOptions();
            if (error) {
                toast.error(`Error loading customer companies: ${error}`);
                setCustomerOptions([]);
            } else {
                setCustomerOptions(
                    Array.isArray(data)
                        ? data.map((item: any) => ({
                            label: item.customer_company_name ?? item.name,
                            value: item.id,
                        }))
                        : []
                );
            }
        } catch {
            toast.error("Failed to load customer company options");
            setCustomerOptions([]);
        } finally {
            setIsCustomerLoading(false);
        }
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        setIsError(false);
        try {
            const data = await fetchReconCustomerMappings();
            setTableData(data);
        } catch {
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        loadBrokerOptions();
        loadCustomerOptions();
    }, [loadBrokerOptions, loadCustomerOptions]);


    const updateFormData = (field: keyof ReconCustomerMappingFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (touched[field]) {
            setFormErrors((prev) => ({
                ...prev,
                [field]: validateField(field, value) || undefined,
            }));
        }
    };

    const handleFocus = (field: keyof ReconCustomerMappingFormData) => {
        setTouched((prev) => ({ ...prev, [field]: true }));
    };


    const handleSubmit = async () => {
        const allTouched: ReconCustomerMappingFormTouched = {
            customer_company: true,
            broker: true,
            external_client_code: true,
        };
        setTouched(allTouched);
        const errors = validateAll(formData);
        setFormErrors(errors);
        if (Object.keys(errors).length > 0) {
            toast.error("Please fill in all required fields.");
            return;
        }
        setIsSaving(true);
        try {
            const created = await createReconCustomerMapping(formData);
            setTableData((prev) => [...prev, created]);
            toast.success("Customer mapping created successfully!");
            setFormData(INITIAL_FORM);
            setTouched({});
            setFormErrors({});
        } catch (error) {
            toast.error(extractErrorMessage(error));
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleFileUploadSubmit = async (file: File) => {
        return await handleImportFileUpload(file, importReconCustomerMappings, loadData);
    };

    const handleCustomerDownload = () =>
        handleDataDownload(EXPORT_URL, "all_customers_mapping.csv");


    const handleClear = () => {
        setFormData(INITIAL_FORM);
        setFormErrors({});
        setTouched({});
    };

    const handleCopyRow = (row: MRT_Row<ReconCustomerMapping>) => {
        setFormData({
            customer_company: row.original.customer_company,
            broker: row.original.broker,
            external_client_code: row.original.external_client_code,
        });
        toast.success("Copied successfully!");
    };



    const handleDeleteRow = (row: MRT_Row<ReconCustomerMapping>) => {
        showDeleteConfirmModal({
            title: "Are you sure you want to delete this entry?",
            content: `Deleting ${row.original.customer_company_name} - ${row.original.broker_name}`,
            onOk: () => {
                setIsSaving(true);
                deleteReconCustomerMapping(row.original.id)
                    .then(() => {
                        setTableData((prev) =>
                            prev.filter((item) => item.id !== row.original.id)
                        );
                        toast.success("Customer mapping deleted successfully!");
                    })
                    .catch(() => toast.error("Failed to delete. Please try again."))
                    .finally(() => setIsSaving(false));
            },
        });
    };

    const handleBulkDelete = () => {
        const selectedIds = Object.keys(rowSelection);
        if (selectedIds.length === 0) return;

        showDeleteConfirmModal({
            title: "Are you sure you want to delete selected records?",
            content: `Deleting ${selectedIds.length} record(s)...`,
            onOk: () => {
                setIsSaving(true);
                bulkDeleteReconCustomerMappings(selectedIds)
                    .then(() => {
                        setTableData((prev) =>
                            prev.filter((item) => !selectedIds.includes(item.id))
                        );
                        setRowSelection({});
                        toast.success(`${selectedIds.length} record(s) deleted successfully.`);
                    })
                    .catch(() => toast.error("Bulk delete failed. Please try again."))
                    .finally(() => setIsSaving(false));
            },
        });
    };

    const handleEditingRowSave: MRT_TableOptions<ReconCustomerMapping>["onEditingRowSave"] =
        async ({ row, values, table }) => {
            const errors = validateAll({
                customer_company: values.customer_company,
                broker: values.broker,
                external_client_code: values.external_client_code,
            });
            if (Object.keys(errors).length > 0) {
                toast.error("Please fill in all required fields.");
                return;
            }
            setIsSaving(true);
            try {
                const updated = await updateReconCustomerMapping({
                    id: row.original.id,
                    customer_company: values.customer_company,  // UUID
                    broker: values.broker,                       // UUID
                    external_client_code: values.external_client_code,
                });
                setTableData((prev) =>
                    prev.map((item) => (item.id === updated.id ? updated : item))
                );
                toast.success("Customer mapping updated successfully!");
                table.setEditingRow(null);
            } catch (error) {
                toast.error(extractErrorMessage(error));
            } finally {
                setIsSaving(false);
            }
        };

    const columns: MRT_ColumnDef<ReconCustomerMapping>[] = [
        {
            accessorKey: "broker",
            header: "Broker Name",
            Cell: ({ row }) => row.original.broker_name,
            editVariant: "select",
            editSelectOptions: brokerOptions,
            size: 200,
            minSize: 150,
        },
        {
            accessorKey: "customer_company",
            header: "Customer Company",
            Cell: ({ row }) => row.original.customer_company_name,
            editVariant: "select",
            editSelectOptions: customerOptions,
            size: 220,
            minSize: 150,
        },
        {
            accessorKey: "external_client_code",
            header: "External Client Code",
            size:200,
            minSize: 150,

        },
    ];


    const table = useMaterialReactTable({
        columns,
        data: tableData,
        enableColumnPinning: true,
        enableDensityToggle: false,
        enableColumnResizing: true,
        enableEditing: true,
        editDisplayMode: "row",
        onEditingRowSave: handleEditingRowSave,
        onEditingRowCancel: ({ table }) => table.setEditingRow(null),
        enableColumnOrdering: true,
        enableRowOrdering: false,
        enableRowSelection: true,
        onRowSelectionChange: setRowSelection,

        getRowId: (row) => row.id,
        enableColumnFilterModes: true,
        initialState: { showColumnFilters: true },
        filterFns: {
            customFilterFn: (row, id, filterValue) =>
                row.getValue(id) === filterValue,
        },
        muiTableHeadCellProps: {
            sx: {
                border: "1px solid #e0e0e087",
                fontStyle: "normal",
                fontWeight: "normal",
            },
        },
        muiTopToolbarProps: () => ({
            sx: {
                backgroundColor: "#fff",
                padding: "8px",
                "& .MuiBox-root.css-wsew38": {
                    backgroundColor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                },
            },
            className: "top-toolbar-wrapper__new",
        }),
        muiEditTextFieldProps: {
            sx: {
                "& .MuiInputBase-root": { fontSize: "0.7rem", height: "32px" },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                "& .MuiSelect-select": { fontSize: "0.7rem", padding: "4px 8px" },
                minWidth: "120px",
            },
        },
        muiTableBodyCellProps: {
            sx: { border: "1px solid #ebe9e98f" },
        },
        muiTablePaperProps: {
            elevation: 0,
            sx: { borderRadius: "0", border: "1" },
        },
        displayColumnDefOptions: {
            "mrt-row-actions": {
                size: 115,
                minSize: 115,
            },
            "mrt-row-select": {
                size: 40,
                minSize: 40,
            },
        },
        localization: {
            filterCustomFilterFn: "Custom Filter Fn",
        } as any,
        muiToolbarAlertBannerProps: isError
            ? { color: "error", children: "Error loading data" }
            : undefined,
        muiTableContainerProps: () => ({
            sx: { backgroundColor: "#fff" },
            className: "table-container-wrapper__new",
        }),
        muiTableFooterProps: () => ({
            sx: { backgroundColor: "#fff" },
            className: "table-footer-wrapper__new",
        }),
        muiBottomToolbarProps: () => ({
            sx: { backgroundColor: "#fff", padding: "8px" },
            className: "bottom-toolbar-wrapper__new",
        }),
        enableRowActions: true,
        renderRowActions: ({ row, table }) => (
            <Box sx={{ display: "flex", gap: "1rem" }}>
                <Tooltip title="Edit" className="editIcon">
                    <IconButton onClick={() => table.setEditingRow(row)}>
                        <EditIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete" className="deleteIcon">
                    <IconButton color="error" onClick={() => handleDeleteRow(row)}>
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Copy Row">
                    <IconButton onClick={() => handleCopyRow(row)}>
                        <ContentCopyIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        ),

        renderTopToolbarCustomActions: ({ table }) => (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button startIcon={<UploadFileIcon />} onClick={handleFileUploadOpen}>
                    Upload File
                </Button>
                <Button
                    onClick={() => handleCustomerDownload()}
                    startIcon={<BrowserUpdatedIcon />}
                >
                    Export All Data
                </Button>
                {Object.keys(rowSelection).length > 0 && (
                    <Button
                        variant="text"
                        color="error"
                        disabled={isSaving}
                        onClick={handleBulkDelete}
                        sx={{
                            textTransform: "none",
                            fontSize: "0.75rem",
                            height: "32px",
                            color: "#d32f2f",
                            fontWeight: 600,
                        }}
                    >
                        Delete Selected ({Object.keys(rowSelection).length})
                    </Button>
                )}
                <BrokerTemplateDownloadButton
                    apiEndpoint="/paper-app/download-customer-mapping-template/"
                    buttonText="Download Template"
                    fileName="customerMapping_template.csv"
                />
            </Box>
        ),

        state: {
            isLoading,
            isSaving,
            showAlertBanner: isError,
            showProgressBars: isLoading,
            showColumnFilters: true,
            rowSelection,
        },
    });


    const inputSx = {
        flex: { xs: "1 1 auto", sm: "1 1 70%" },
        "& .MuiInputBase-root": {
            fontSize: "0.7rem",
            height: { xs: "36px", sm: "32px" },
        },
        "& .MuiInputLabel-root": {
            fontSize: "0.7rem",
            transform: "translate(14px, 8px) scale(1)",
            "&.MuiInputLabel-shrink": {
                transform: "translate(14px, -6px) scale(0.75)",
            },
        },
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#C0C0C0" },
        maxWidth: { sm: "250px", xs: "100%" },
    };


    return (
        <div className="page-body">
            <Container fluid>
                <Row>
                    <div>
                        <div className="table-ad">
                            <Accordion defaultActiveKey="0" className="table-ad__addInputs">
                                <Accordion.Item eventKey="0">
                                    <Accordion.Header className="accordion_button">
                                        Reconciliation Customer Mapping
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <div className="form_contain">
                                            <div className="flex formWrapper">
                                                <Box
                                                    sx={{
                                                        width: "100%",
                                                        p: { xs: 1, sm: 1 },
                                                        pt: "20px !important",
                                                        backgroundColor: "#ffff",
                                                        borderRadius: "4px",
                                                        border: "1px solid #e0e0e0",
                                                    }}
                                                >
                                                    <Box
                                                        component="form"
                                                        className="form_default"
                                                        sx={{
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            alignItems: "flex-start",
                                                            justifyContent: "start",
                                                            gap: 0.5,
                                                            "& > *": {
                                                                minWidth: {
                                                                    xs: "100%",
                                                                    sm: "48%",
                                                                    md: "18%",
                                                                    lg: "12%",
                                                                    xl: "7.5%",
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        
                                                        <CompactSelect
                                                            label="Broker"
                                                            value={formData.broker}
                                                            onChange={(value) => updateFormData("broker", value)}
                                                            options={brokerOptions}
                                                            disabled={isBrokerLoading}
                                                            name="broker"
                                                            error={Boolean(
                                                                formErrors.broker && touched.broker
                                                            )}
                                                            validateOnBlur={(value) =>
                                                                validateField("broker", value)
                                                            }
                                                            slotProps={{ inputLabel: { shrink: true } }}
                                                            onFocus={() => handleFocus("broker")}
                                                            sx={inputSx}
                                                        />

                                                        <CompactSelect
                                                            label="Customer Company"
                                                            value={formData.customer_company}
                                                            onChange={(value) => updateFormData("customer_company", value)}
                                                            name="customer_company"
                                                            error={Boolean(formErrors.customer_company && touched.customer_company)}
                                                            validateOnBlur={(value) => validateField("customer_company", value)}
                                                            onFocus={() => handleFocus("customer_company")}
                                                            options={customerOptions}
                                                            disabled={isCustomerLoading}
                                                            slotProps={{ inputLabel: { shrink: true } }}
                                                            sx={inputSx}
                                                        />

                                                        <CompactTextField
                                                            label="External Client Code"
                                                            value={formData.external_client_code}
                                                            onChange={(value) =>
                                                                updateFormData("external_client_code", value)
                                                            }
                                                            name="external_client_code"
                                                            error={Boolean(
                                                                formErrors.external_client_code &&
                                                                touched.external_client_code
                                                            )}
                                                            validateOnBlur={(value) =>
                                                                validateField("external_client_code", value)
                                                            }
                                                            slotProps={{ inputLabel: { shrink: true } }}
                                                            onFocus={() => handleFocus("external_client_code")}
                                                            sx={inputSx}
                                                        />

                                                        <Button
                                                            onClick={handleSubmit}
                                                            variant="contained"
                                                            fullWidth
                                                            disabled={isSaving}
                                                            sx={{
                                                                height: { xs: "36px", sm: "32px" },
                                                                flex: { xs: "1 1 auto", sm: "0 0 auto" },
                                                                minWidth: { xs: "100%", sm: "100px" },
                                                                maxWidth: { sm: "150px" },
                                                                backgroundColor: "#00bcd4",
                                                                textTransform: "none",
                                                                fontSize: "0.75rem",
                                                                "&:hover": { backgroundColor: "#008c9e" },
                                                            }}
                                                        >
                                                            Add
                                                        </Button>

                                                        <Button
                                                            onClick={handleClear}
                                                            variant="outlined"
                                                            fullWidth
                                                            sx={{
                                                                height: { xs: "36px", sm: "32px" },
                                                                flex: { xs: "1 1 auto", sm: "0 0 auto" },
                                                                minWidth: { xs: "100%", sm: "80px" },
                                                                maxWidth: { sm: "120px" },
                                                                textTransform: "none",
                                                                fontSize: "0.75rem",
                                                            }}
                                                        >
                                                            Clear
                                                        </Button>

                                                    </Box>
                                                </Box>
                                            </div>
                                        </div>
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>

                            <div className="table-ad__wrapper">
                                <div className="table-ad__wrapperCard">
                                    <MaterialReactTable table={table} />
                                </div>
                            </div>
                        </div>
                        <FileUpload
                            open={openFileUpload}
                            onFileUploadClose={handleFileUploadClose}
                            onFileUploadSubmit={handleFileUploadSubmit}
                            acceptedFileTypes=".csv"
                        />
                    </div>
                </Row>
            </Container>
        </div>
    );
};

export default ReconciliationCustomerMapping;